import { Component, inject, input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { EMPTY, filter, Observable, of, skipWhile, Subscription, take, throwError } from 'rxjs';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
} from '../../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { ZiroFormConfig } from '../../../../forms/ziro-forms-models';
import {
  ClientErrorReason,
  Microsoft360View,
  MicrosoftSharedVoicemailGroupMembership,
  MicrosoftSharedVoicemailGroupOption,
  MicrosoftSharedVoicemailGroupsFieldConfig,
} from '../../../../shared/models/generated/ziroModels';
import {
  MultiCheckboxOptionConfig,
  ZiroMultiCheckboxComponent,
  ZiroMultiCheckboxConfig,
} from '../../../../forms/fields/multi-checkbox/ziro-multi-checkbox.component';
import { Microsoft360ViewContext } from '../../../../shared/contexts/microsoft-360-view.context';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { MicrosoftSharedVoicemailGroupsContext } from '../../../../shared/contexts/microsoft-shared-voicemail-groups.context';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ToastTypes } from '../../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../../shared/services/toast.service';
import { MicrosoftTenantsStateContext } from '../../../../shared/contexts/microsoft-tenants-state.context';
import { Microsoft360ViewPollingService } from '../../../../shared/services/microsoft/microsoft-360-view-polling.service';

export interface SharedVoicemailGroupEntity {
  sharedVoicemailGroups: {
    [key: string]: boolean;
  };
}

@Component({
  selector: 'ziro-shared-voicemail-groups-memberships-form',
  templateUrl: './shared-voicemail-groups-memberships-form.component.html',
  styleUrls: ['./shared-voicemail-groups-memberships-form.component.scss'],
  providers: [Microsoft360ViewPollingService],
  standalone: false,
})
export class SharedVoicemailGroupsMembershipsFormComponent
  extends ZiroFormAbstractDirective<SharedVoicemailGroupEntity>
  implements OnInit, OnDestroy
{
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _bottomNavService = inject(BottomNavService);
  private _toastService = inject(ToastService);
  private _translateService = inject(TranslateService);
  private _microsoft360ViewContext = inject(Microsoft360ViewContext);
  private _sharedVoicemailGroupsContext = inject(MicrosoftSharedVoicemailGroupsContext);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);
  private _microsoft360ViewPollingService = inject(Microsoft360ViewPollingService);
  readonly fieldConfig = input<MicrosoftSharedVoicemailGroupsFieldConfig>();
  readonly microsoft360View = input<Microsoft360View>();
  readonly inventory = input<MicrosoftSharedVoicemailGroupOption[]>();
  readonly initialEntity = input<SharedVoicemailGroupEntity>();

  @ViewChild('sharedVoicemailGroupsCheckboxes')
  groupsCheckboxes: ZiroMultiCheckboxComponent<SharedVoicemailGroupEntity>;
  formConfig: ZiroFormConfig;
  ziroIcons = ZiroIcons;

  private userPrincipalName: string;
  private _groupLabels: MultiCheckboxOptionConfig[];
  private _tenantId: string;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.userPrincipalName = this.microsoft360View().microsoft365UserLicenses.userPrincipalName;
    this._getCurrentTenant();
    this._setCheckboxes();
    this._initBottomNav();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._microsoft360ViewPollingService.stopPolling();
    super.ngOnDestroy();
  }

  private _getCurrentTenant(): void {
    const tenantSub = this._tenantsStateContext.cachedTenantState$
      .pipe(
        filter((cachedTenant) => !!cachedTenant?.tenantId),
        take(1),
      )
      .subscribe((cachedTenant) => {
        this._tenantId = cachedTenant.tenantId.toString();
      });
    this._subscriptions.add(tenantSub);
  }

  protected submit(): Observable<void> {
    return this._onSaveClick();
  }

  private _onSaveClick(): Observable<void> {
    this._setPending(true);

    return this._updateUserSharedVoicemailGroups().pipe(
      switchMap((updatedMembership) => {
        if (!updatedMembership) {
          return of(null);
        }
        this._microsoft360ViewPollingService.setBaseUrl(this.userPrincipalName, this._tenantId);
        this._microsoft360ViewPollingService.startPolling(Microsoft360ViewPollingService.MAX_POLLS);
        return this._microsoft360ViewPollingService.state$.pipe(
          skipWhile((state: Microsoft360View) => {
            const polledIds = (state.sharedVoicemailGroupMemberships || [])
              .map((group: MicrosoftSharedVoicemailGroupOption) => group.id)
              .sort();
            const updatedIds = updatedMembership.groupMemberships.map((group) => group.id).sort();
            return !isEqual(polledIds, updatedIds);
          }),
        );
      }),
      map((new360View: Microsoft360View) => {
        this._microsoft360ViewPollingService.stopPolling();
        if (new360View) {
          this._microsoft360ViewContext.setMicrosoft360View(new360View);
        }
        this._ziroFormStateService.setIsFormDirty(false);
        this._toastService.push(
          ToastTypes.SUCCESS,
          ZiroIcons.VOICEMAIL_SMALL,
          'tkey;shared.toast.save.success.title',
          this._translateService.instant('tkey;shared.toast.save.success.message', {
            type: this._translateService.instant('tkey;shared.model.microsoft_365.text'),
            name: this.userPrincipalName,
          }),
        );
        this._router.navigate(['../'], { relativeTo: this._route });
      }),
      catchError((response) => {
        this._setPending(false);
        if (response.status === 422 && response.error.reason === ClientErrorReason.GROUP_NOT_PERMITTED) {
          this._toastService.push(
            ToastTypes.ERROR,
            `${ZiroIcons.VOICEMAIL_SMALL} text-danger`,
            'tkey;microsoft_360.view.shared_voicemail_groups.membership.toast.error.title',
            response.error.description,
          );
          return throwError(() => EMPTY);
        }
        return throwError(() => response);
      }),
    );
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'ms-shared-voicemail-groups-cancel',
          dataAutomation: 'ms-shared-voicemail-groups-cancel',
          label: 'tkey;global.button.cancel.text',
          buttonClass: ButtonStyles.DEFAULT,
          cb: () => {
            this._router.navigate(['../'], { relativeTo: this._route });
          },
        },
        {
          id: 'ms-shared-voicemail-groups-save',
          dataAutomation: 'ms-shared-voicemail-groups-save',
          label: 'tkey;global.button.save.text',
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: {
              disabled: false,
              tooltipKey: '',
            },
          },
          icon: ZiroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );
  }

  private _setCheckboxes(): void {
    this._configureForm();
    const sharedVoicemailGroups: { [key: string]: boolean } = {};
    this._groupLabels.forEach((group: MultiCheckboxOptionConfig) => {
      sharedVoicemailGroups[group.label] = (this.microsoft360View().sharedVoicemailGroupMemberships || []).some(
        (userGroup: MicrosoftSharedVoicemailGroupOption) => userGroup.displayName === group.label,
      );
    });
    this.entitySource.next({
      sharedVoicemailGroups: { ...sharedVoicemailGroups },
    });
  }

  private _configureForm(): void {
    const groups = this.inventory().map((item: MicrosoftSharedVoicemailGroupOption) => {
      const label = item.displayName;
      let description = item.description || ' ';
      if (!label) {
        description += this._translateService.instant('tkey;selfserve.microsoft365.checkbox.notfound');
      }
      return {
        label,
        additionalContent: description,
        alignLeft: true,
        disabled: () => this._isGroupReadOnly(item),
        disabledTooltip: () => this._getDisabledTooltip(item),
      } as MultiCheckboxOptionConfig;
    });
    this._groupLabels = groups;
    this.formConfig = {
      fields: {
        sharedVoicemailGroups: {
          dataAutomation: 'microsoft-shared-voicemail-groups-checkbox',
          componentConfig: new ZiroMultiCheckboxConfig({ multiCheckboxOptionConfig: groups }),
        },
      },
      options: {
        columnClasses: {
          label: 'col-lg-3',
          input: 'col-lg-9',
        },
      },
    };
  }

  private _isGroupReadOnly(group: MicrosoftSharedVoicemailGroupOption): boolean {
    return this.fieldConfig().readOnlyGroupList.some((readOnlyGroup) => readOnlyGroup.id === group.id);
  }

  private _getDisabledTooltip(group: MicrosoftSharedVoicemailGroupOption): string {
    if (this._isGroupReadOnly(group)) {
      return 'tkey;microsoft_360.view.shared_voicemail_groups.membership.checkbox.disabled';
    }
    return '';
  }

  private _updateUserSharedVoicemailGroups(): Observable<MicrosoftSharedVoicemailGroupMembership> {
    if (isEqual(this.initialEntity(), this.formData)) {
      return of(null);
    }
    const newGroupsForUser = this.inventory()
      .filter((item) => this.formData.sharedVoicemailGroups[item.displayName])
      .map((option: MicrosoftSharedVoicemailGroupOption) => ({
        id: option.id,
        displayName: option.displayName,
        description: option.description,
      }));
    const groupIdsInForm = this.fieldConfig()
      .writeGroupList.concat(this.fieldConfig().readOnlyGroupList)
      .map((group) => group.id);
    const groupsToPreserve = (this.microsoft360View().sharedVoicemailGroupMemberships || []).filter(
      (group: MicrosoftSharedVoicemailGroupOption) => !groupIdsInForm.includes(group.id),
    );
    const update: MicrosoftSharedVoicemailGroupMembership = {
      userPrincipalName: this.userPrincipalName,
      groupMemberships: [...newGroupsForUser, ...groupsToPreserve],
    };
    return this._sharedVoicemailGroupsContext
      .updateUserGroups(this._tenantId, this.userPrincipalName, update)
      .pipe(map(() => update));
  }

  private _setPending(setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'ms-shared-voicemail-groups-save',
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }
}
